<script lang="ts" setup>
import { type ApiToken } from '@/types'
import { STATUS_CODES } from '@/constants'
import { ref } from 'vue'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import BaseDeleteModal from '@/components/base/BaseDeleteModal.vue'

const props = defineProps<{
  show: boolean
  token: ApiToken
}>()

const emits = defineEmits<{
  (e: 'close'): void
  (e: 'deleted', token: ApiToken): void
}>()

const loading = ref(false)

const handleClose = () => {
  emits('close')
}

const handleDelete = async () => {
  loading.value = true

  try {
    const res = await useAxios<null, null>(
      'DELETE',
      `/account/tokens/${props.token.id}`,
      true
    )

    if (res && (res.status === STATUS_CODES.NO_CONTENT || res.status === STATUS_CODES.OK)) {
      emits('deleted', props.token)
      handleClose()
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'ApiTokenDeleteModal' })
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <BaseDeleteModal
    :show="show"
    :title="$t('dashboard.account.apiTokens.delete.title')"
    :subtitle="token.name"
    :confirm-text="$t('dashboard.account.apiTokens.delete.message')"
    :warning-text="$t('dashboard.account.apiTokens.delete.warning')"
    :loading="loading"
    :delete-button-text="$t('dashboard.account.apiTokens.delete.confirm')"
    @delete="handleDelete"
    @close="handleClose"
  >
    <template #preview>
      <div class="preview-row">
        <span class="preview-label">{{ $t('dashboard.account.apiTokens.columns.name') }}</span>
        <code>{{ token.name }}</code>
      </div>
    </template>
  </BaseDeleteModal>
</template>
